import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a dynamic product title.
 * This component listens for variant update events and dynamically appends
 * a specific option value (like device name or size) to the core product title.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} titleText - The span element containing the title text
 *
 * @extends {Component<Refs>}
 */
class ProductTitleComponent extends Component {
  requiredRefs = ['titleText'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.updateTitle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.updateTitle);
  }

  /**
   * Updates the title.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateTitle = (event) => {
    // Ensure we are operating on the correct product context
    if (event.detail.data && event.detail.data.newProduct) {
      if (this.dataset.productId && this.dataset.productId !== String(event.detail.data.newProduct.id)) {
        return;
      }
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId) {
      if (this.dataset.productId && event.target.dataset.productId !== this.dataset.productId) {
        return;
      }
    }

    if (event.detail.resource) {
      const variant = event.detail.resource;
      const baseTitle = this.dataset.baseTitle || '';
      const separator = this.dataset.separator || '';
      const optionIndex = parseInt(this.dataset.optionIndex, 10);
      
      // Shopify variant JSON doesn't always have an 'options' array, but uses option1, option2, option3
      const dynamicValue = variant.options 
        ? variant.options[optionIndex]
        : variant[`option${optionIndex + 1}`];

      // Revert to base title if no matching option index is defined
      if (isNaN(optionIndex) || optionIndex < 0 || !dynamicValue) {
        this.refs.titleText.textContent = baseTitle;
        return;
      }

      // Update the DOM to display tracking string
      if (dynamicValue && dynamicValue !== 'Default Title') {
        this.refs.titleText.textContent = `${baseTitle}${separator}${dynamicValue}`;
      } else {
        this.refs.titleText.textContent = baseTitle;
      }
    }
  };
}

if (!customElements.get('product-title-component')) {
  customElements.define('product-title-component', ProductTitleComponent);
}
